/**
 * NFT identity ledger — separate from FIFO/tax rows.
 *
 * Tax/FIFO events stay in dataset.nftTaxRows (one VTHO disposal per payment).
 * Per-NFT cost basis lives in dataset.nftAcquisitionRows (one row per token).
 */

import { isInternalNftAcquisitionAllocationRow } from "./taxExportEligibility.js";

const ACQUISITION_TAX_CLASSES = new Set([
  "nft_mint",
  "mint",
  "nft_galaxy_mint",
  "galaxy_mint",
  "nft_marketplace_purchase_payment",
  "nft_transform_acquisition",
]);

export const NFT_TAX_PRIMARY_DISPLAY_CLASSES = new Set([
  "nft_mint",
  "nft_galaxy_mint",
  "nft_marketplace_purchase_payment",
  "nft_transform",
  "sale_payment",
  "nft_marketplace_payout_review",
]);

export function isNftTaxPrimaryDisplayRow(row = {}) {
  const cls = String(row.classification || row.type || "")
    .trim()
    .toLowerCase();
  return NFT_TAX_PRIMARY_DISPLAY_CLASSES.has(cls);
}

const AMOUNT_EPS = 1e-6;

export function isNftAcquisitionAllocationRow(row = {}) {
  return isInternalNftAcquisitionAllocationRow(row);
}

export function pickNftIdentityKey(row = {}) {
  const contract = String(
    row.nftContract ||
      row.nft_contract ||
      row.contract ||
      row.contractAddress ||
      row.contract_address ||
      ""
  ).toLowerCase();
  const tokenId = String(
    row.tokenId ??
      row.nft_token_id ??
      row.token_id ??
      row.relatedMintTokenId ??
      row.related_mint_token_id ??
      ""
  ).trim();
  if (!contract || !tokenId) return "";
  return `${contract}|${tokenId}`;
}

function identityTokenId(entry) {
  if (entry == null || typeof entry !== "object") return entry;
  return entry.tokenId ?? entry.nft_token_id ?? entry.token_id;
}

export function extractNftBatchTokenIds(row = {}) {
  if (Array.isArray(row.nftBatchTokenIds) && row.nftBatchTokenIds.length) {
    return [
      ...new Set(
        row.nftBatchTokenIds
          .map((id) => (id == null ? "" : String(id).trim()))
          .filter(Boolean)
      ),
    ];
  }

  const identities = Array.isArray(row.nftBatchIdentities)
    ? row.nftBatchIdentities
    : Array.isArray(row.vemugsBatchProof?.nftBatchIdentities)
      ? row.vemugsBatchProof.nftBatchIdentities
      : [];

  return [
    ...new Set(
      identities
        .map(identityTokenId)
        .filter((id) => id != null && String(id).trim() !== "")
        .map((id) => String(id).trim())
    ),
  ];
}

export function nftBatchSizeOf(row = {}) {
  const n = Number(
    row.nftBatchSize ?? row.allocationCount ?? row.vemugsBatchProof?.batchSize ?? 0
  );
  const ids = extractNftBatchTokenIds(row);
  if (n > 1) return n;
  if (ids.length > 1) return ids.length;
  return n || ids.length || 0;
}

export function parentPaymentFamilyKey(row = {}) {
  const fromParent = String(row.parentPaymentEventKey || "").trim();
  if (fromParent.includes("|")) return fromParent.toLowerCase();

  const paymentHash = String(
    row.nftBatchPaymentHash ||
      row.vemugsBatchProof?.paymentHash ||
      row.hash ||
      row.txHash ||
      ""
  ).toLowerCase();
  const token = String(
    row.token || row.nftPaymentToken || row.token_symbol || row.tokenSymbol || ""
  ).toUpperCase();
  if (!paymentHash || paymentHash === "unknown" || !token) return "";
  return `${paymentHash}|${token}`;
}

function isBatchIdentityChild(row = {}) {
  return (
    Number(row.allocationCount || 0) > 1 ||
    Number(row.nftBatchSize || 0) > 1 ||
    Number(row.batchSize || 0) > 1
  );
}

function expectedAcquisitionChildCount(parent = {}) {
  return Math.max(
    Number(parent.nftBatchSize || 0),
    Number(parent.allocationCount || 0),
    extractNftBatchTokenIds(parent).length,
    Array.isArray(parent.nftBatchIdentities)
      ? parent.nftBatchIdentities.length
      : Array.isArray(parent.vemugsBatchProof?.nftBatchIdentities)
        ? parent.vemugsBatchProof.nftBatchIdentities.length
        : 0
  );
}

function recoverTokenId(row = {}) {
  const direct =
    row.tokenId ??
    row.nft_token_id ??
    row.token_id ??
    row.relatedMintTokenId ??
    row.related_mint_token_id;
  if (direct != null && String(direct).trim() !== "") return String(direct).trim();
  const ids = extractNftBatchTokenIds(row);
  return ids[0] ? String(ids[0]) : null;
}

export function matchAcquisitionChildren(parent = {}, acquisitionRows = []) {
  const familyKey = parentPaymentFamilyKey(parent);
  const paymentHash = String(
    parent.nftBatchPaymentHash || parent.hash || parent.txHash || ""
  ).toLowerCase();
  const parentKey = String(parent.parentPaymentEventKey || "").toLowerCase();
  const parentIsBatch = expectedAcquisitionChildCount(parent) > 1;

  const matched = [];
  for (const child of Array.isArray(acquisitionRows) ? acquisitionRows : []) {
    const childIsBatch = isBatchIdentityChild(child);
    if (parentIsBatch && !childIsBatch) continue;
    if (!parentIsBatch && childIsBatch) continue;

    const childFamily = String(child.parentPaymentEventKey || "").toLowerCase();
    const childPay = String(
      child.nftBatchPaymentHash || child.paymentHash || ""
    ).toLowerCase();
    const childEconomic = String(child.economicEventId || "").toLowerCase();

    const hit =
      (parentKey && childFamily === parentKey) ||
      (familyKey && childFamily === familyKey) ||
      (familyKey && parentPaymentFamilyKey(child) === familyKey) ||
      (paymentHash && childPay === paymentHash) ||
      (paymentHash &&
        childEconomic &&
        (childEconomic === `vemugs-brew:${paymentHash}` ||
          childEconomic === `nft-payment:${paymentHash}`));

    if (hit) matched.push(child);
  }

  matched.sort(
    (a, b) => (Number(a.allocationIndex) || 0) - (Number(b.allocationIndex) || 0)
  );

  const seen = new Set();
  return matched.filter((child) => {
    const key =
      pickNftIdentityKey(child) ||
      `${String(child.hash || "")}|${String(child.tokenId ?? "")}|${child.allocationIndex ?? ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function synthesizeAcquisitionChildrenFromParent(parent = {}) {
  const identities = Array.isArray(parent.nftBatchIdentities)
    ? parent.nftBatchIdentities
    : Array.isArray(parent.vemugsBatchProof?.nftBatchIdentities)
      ? parent.vemugsBatchProof.nftBatchIdentities
      : [];
  const tokenIds = extractNftBatchTokenIds(parent);
  const recoveredSingle =
    parent.tokenId ??
    parent.nft_token_id ??
    parent.token_id ??
    parent.relatedMintTokenId ??
    parent.related_mint_token_id ??
    tokenIds[0] ??
    identities[0]?.tokenId ??
    identities[0]?.nft_token_id ??
    identities[0]?.token_id ??
    null;

  const size = Math.max(
    Number(parent.nftBatchSize || 0),
    identities.length,
    tokenIds.length,
    recoveredSingle != null && String(recoveredSingle).trim() !== "" ? 1 : 0
  );
  // Need at least one recoverable NFT identity.
  if (!(size >= 1)) return [];

  const totalQty = Number(
    parent.nftBatchTotalPaymentVtho ?? parent.amount ?? parent.nftPaymentAmount ?? 0
  );
  const unitQty =
    Number(parent.nftUnitPaymentVtho) > 0
      ? Number(parent.nftUnitPaymentVtho)
      : size > 0
        ? totalQty / size
        : 0;
  const totalFiat = Number(
    parent.nftAcquisitionCostFiat ??
      parent.euroAmount ??
      parent.nftPurchaseValueFiat ??
      0
  );
  const unitFiat = size > 0 && Number.isFinite(totalFiat) ? totalFiat / size : 0;
  const token = String(parent.token || parent.nftPaymentToken || "VTHO").toUpperCase();
  const paymentHash =
    parent.nftBatchPaymentHash || parent.hash || parent.txHash || null;
  const nftName = parent.nftName || parent.nft_name || parent.name || "NFT";

  return Array.from({ length: size }, (_, idx) => {
    const identity = identities[idx] || {};
    const tokenId =
      identity.tokenId ??
      identity.nft_token_id ??
      identity.token_id ??
      tokenIds[idx] ??
      (idx === 0 ? recoveredSingle : null);
    if (tokenId == null || String(tokenId).trim() === "") return null;
    const contract =
      identity.nftContract ||
      identity.nft_contract ||
      identity.contract ||
      parent.nftContract ||
      parent.nft_contract ||
      parent.contract ||
      null;

    return {
      classification: "nft_acquisition_allocation",
      allocationRecordOnly: true,
      token,
      amount: unitQty,
      nftPaymentAmount: unitQty,
      nftPaymentToken: token,
      nftAcquisitionCostFiat: unitFiat,
      euroAmount: unitFiat,
      nftName: identity.nftName || identity.name || nftName,
      nftContract: contract,
      tokenId: String(tokenId),
      hash: identity.hash || parent.nftBatchMintHash || parent.hash,
      nftBatchMintHash: parent.nftBatchMintHash || identity.hash || null,
      nftBatchPaymentHash: paymentHash,
      parentPaymentEventKey: parentPaymentFamilyKey(parent),
      allocationIndex: idx,
      allocationCount: size,
      nftBatchSize: size,
      timestamp: identity.timestamp || parent.timestamp || null,
    };
  }).filter(Boolean);
}

export function resolveAcquisitionChildren(parent = {}, acquisitionRows = []) {
  const matched = matchAcquisitionChildren(parent, acquisitionRows);
  const expected = expectedAcquisitionChildCount(parent);
  // Prefer a complete set: incomplete year-filtered matches must not hide identities.
  if (matched.length > 0 && (expected <= 1 || matched.length >= expected)) {
    return matched;
  }
  const synthesized = synthesizeAcquisitionChildrenFromParent(parent);
  if (synthesized.length > matched.length) return synthesized;
  if (matched.length > 0) return matched;
  return synthesized;
}

/**
 * Map one NFT identity / allocation into a normal NftTaxTable mint row.
 * Looks like Thorheads / any other single NFT mint (expandable + disposal P/L).
 *
 * When `parentTaxRow` is provided (VeMugs batch/single payment), FIFO legs and
 * taxable P/L are copied proportionally for UI only. Totals still come from
 * the parent once via dataset.nftTotals — display rows stay excludedFromTaxTotals.
 */
export function toNftTaxTableIdentityDisplayRow(row = {}, parentTaxRow = null) {
  const amount = Number(
    row.nftPaymentAmount ?? row.paymentAmount ?? row.amount ?? 0
  );
  const fiat = Number(
    row.nftAcquisitionCostFiat ??
      row.nftAcquisitionCostEUR ??
      row.acquisitionCostFiat ??
      row.euroAmount ??
      0
  );
  const token = String(
    row.nftPaymentToken || row.paymentAsset || row.token || "VTHO"
  ).toUpperCase();
  const unitPrice =
    amount > 0 && Number.isFinite(fiat) ? fiat / amount : Number(row.euroPrice || 0);

  const parent = parentTaxRow && typeof parentTaxRow === "object" ? parentTaxRow : null;
  const parentAmount = Number(
    parent?.nftBatchTotalPaymentVtho ??
      parent?.amount ??
      parent?.quantity ??
      parent?.qty ??
      0
  );
  const share =
    parent && parentAmount > 0
      ? amount / parentAmount
      : Number(row.nftBatchSize || row.allocationCount || 0) > 1
        ? 1 / Number(row.nftBatchSize || row.allocationCount)
        : 1;

  const scale = (v) => {
    if (v === null || v === undefined || v === "") return v;
    const n = Number(v);
    if (!Number.isFinite(n)) return v;
    return Number((n * share).toFixed(12));
  };

  const parentLegs = Array.isArray(parent?.fifoLegs)
    ? parent.fifoLegs
    : Array.isArray(parent?.matchedInflows)
      ? parent.matchedInflows
      : [];

  const fifoLegs =
    parentLegs.length > 0
      ? parentLegs.map((leg) => {
          const qty = Number(leg.qty ?? leg.amount ?? leg.quantity ?? 0) * share;
          return {
            ...leg,
            qty,
            amount: qty,
            quantity: qty,
            buyEUR: scale(leg.buyEUR),
            buyFiat: scale(leg.buyFiat ?? leg.buyEUR),
            sellEUR: scale(leg.sellEUR),
            sellFiat: scale(leg.sellFiat ?? leg.sellEUR),
            legProfitEUR: scale(leg.legProfitEUR),
            legProfitFiat: scale(leg.legProfitFiat ?? leg.legProfitEUR),
            taxableProfitEUR: scale(leg.taxableProfitEUR),
            taxableProfitFiat: scale(leg.taxableProfitFiat ?? leg.taxableProfitEUR),
            nonTaxableProfitEUR: scale(leg.nonTaxableProfitEUR),
            nonTaxableProfitFiat: scale(
              leg.nonTaxableProfitFiat ?? leg.nonTaxableProfitEUR
            ),
          };
        })
      : Array.isArray(row.fifoLegs)
        ? row.fifoLegs
        : [];

  const taxableProfit =
    parent != null
      ? scale(
          parent.taxableProfitEUR ??
            parent.taxableProfitFiat ??
            parent.taxableProfit ??
            0
        )
      : row.taxableProfitEUR ?? row.taxableProfitFiat ?? row.taxableProfit ?? null;

  const paymentHash =
    row.paymentHash ||
    row.nftBatchPaymentHash ||
    parent?.nftBatchPaymentHash ||
    parent?.hash ||
    null;

  const tokenId = recoverTokenId(row);
  const amountNum = Number.isFinite(amount) ? amount : 0;

  return {
    ...row,
    ...(parent
      ? {
          isFullyTaxFree: parent.isFullyTaxFree,
          holdingDaysMin: parent.holdingDaysMin,
          holdingDaysMax: parent.holdingDaysMax,
          costBasisMethod: parent.costBasisMethod,
          countryCode: parent.countryCode,
          isTaxable: parent.isTaxable,
          taxable: parent.taxable,
        }
      : {}),
    rawClassification:
      row.rawClassification || row.classification || "nft_acquisition_allocation",
    classification: "nft_mint",
    type: "nft_mint",
    uiNftIdentityRow: true,
    // Display-only share of the parent disposal — never re-count in section totals.
    allocationRecordOnly: false,
    excludedFromTaxTotals: true,
    includedInTaxTotals: false,
    uiProportionalFromBatchParent: parent != null,
    token,
    amount: amountNum,
    quantity: amountNum,
    qty: amountNum,
    nftPaymentAmount: amountNum,
    nftPaymentToken: token,
    euroPrice: parent != null ? Number(parent.euroPrice ?? unitPrice) : unitPrice,
    euroAmount: fiat > 0 ? fiat : parent != null ? scale(parent.euroAmount) : fiat,
    nftAcquisitionCostFiat: fiat,
    nftAcquisitionCostEUR: fiat,
    taxableProfit,
    taxableProfitEUR: taxableProfit,
    taxableProfitFiat: taxableProfit,
    nonTaxableProfitEUR:
      parent != null ? scale(parent.nonTaxableProfitEUR) : row.nonTaxableProfitEUR ?? null,
    nonTaxableProfitFiat:
      parent != null
        ? scale(parent.nonTaxableProfitFiat ?? parent.nonTaxableProfitEUR)
        : row.nonTaxableProfitFiat ?? null,
    grossBuyEUR: parent != null ? scale(parent.grossBuyEUR) : row.grossBuyEUR ?? null,
    grossBuyFiat:
      parent != null
        ? scale(parent.grossBuyFiat ?? parent.grossBuyEUR)
        : row.grossBuyFiat ?? null,
    grossSellEUR: parent != null ? scale(parent.grossSellEUR) : row.grossSellEUR ?? null,
    grossSellFiat:
      parent != null
        ? scale(parent.grossSellFiat ?? parent.grossSellEUR)
        : row.grossSellFiat ?? null,
    grossProfitEUR:
      parent != null ? scale(parent.grossProfitEUR) : row.grossProfitEUR ?? null,
    grossProfitFiat:
      parent != null
        ? scale(parent.grossProfitFiat ?? parent.grossProfitEUR)
        : row.grossProfitFiat ?? null,
    shortTermProfitEUR:
      parent != null ? scale(parent.shortTermProfitEUR) : row.shortTermProfitEUR ?? null,
    shortTermProfitFiat:
      parent != null
        ? scale(parent.shortTermProfitFiat ?? parent.shortTermProfitEUR)
        : row.shortTermProfitFiat ?? null,
    longTermProfitEUR:
      parent != null ? scale(parent.longTermProfitEUR) : row.longTermProfitEUR ?? null,
    longTermProfitFiat:
      parent != null
        ? scale(parent.longTermProfitFiat ?? parent.longTermProfitEUR)
        : row.longTermProfitFiat ?? null,
    fifoLegs,
    matchedInflows: fifoLegs,
    tokenId,
    nft_token_id: tokenId,
    token_id: tokenId,
    relatedMintTokenId: tokenId,
    // Same payment hash as other mint-payment rows (FIFO lives on the payment).
    hash: paymentHash || row.mintHash || row.nftBatchMintHash || row.hash || row.acquisitionTxHash,
    paymentHash,
    mintHash: row.mintHash || row.nftBatchMintHash || row.hash || null,
    timestamp: parent?.timestamp || row.timestamp || row.mintTimestamp || null,
  };
}

function firstFiniteOrNull(...values) {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const number = Number(value);
    if (Number.isFinite(number)) return number;
  }
  return null;
}

export function toNftTransformDisplayParent(row = {}) {
  const resultTokenId =
    row.nftTransformResultTokenId ??
    row.nftTransformAcquisition?.tokenId ??
    row.nftTransformResultNft?.tokenId ??
    row.tokenId ??
    null;
  const resultFmv = firstFiniteOrNull(row.resultFmvFiat);
  const transformValue = firstFiniteOrNull(
    row.totalExchangeValueFiat,
    row.grossSellFiat,
    resultFmv,
    row.sourceFmvTotalFiat
  );
  const inputCount =
    Number(row.nftTransformInputCount) ||
    (Array.isArray(row.sourceAcquisitionLegs)
      ? row.sourceAcquisitionLegs.length
      : 0) ||
    (Array.isArray(row.nftTransformDisposals)
      ? row.nftTransformDisposals.filter(
          (d) => d?.nftTransformAggregateDisposal !== true
        ).length || row.nftTransformDisposals.length
      : 3);
  const outputCount = Number(row.nftTransformOutputCount) || 1;

  return {
    ...row,
    classification: "nft_transform",
    type: "nft_transform",
    uiTransformParent: true,
    hash: row.nftTransformBurnHash || row.hash || null,
    timestamp:
      row.nftTransformBurnTs ||
      row.timestamp ||
      row.nftTransformMintTs ||
      null,
    tokenId: resultTokenId,
    relatedMintTokenId: resultTokenId,
    token: "NFT",
    displayToken: "NFT",
    transformDisplayAmount: `${inputCount} → ${outputCount}`,
    unitPriceFiat: firstFiniteOrNull(resultFmv, transformValue),
    amountFiat: transformValue,
    euroPrice: firstFiniteOrNull(resultFmv, transformValue),
    euroAmount: transformValue,
  };
}

/**
 * NftTaxTable rows: normal acquisition batches are flattened per NFT identity,
 * but each 3→1 transform remains exactly one economic parent. Its three
 * disposal children stay attached as expandable tax details and never become
 * primary display rows.
 */
export function buildNftTaxTableDisplayRows(
  taxRows = [],
  acquisitionRows = []
) {
  const out = [];
  const usedIdentityKeys = new Set();
  const usedTransformLifecycles = new Set();

  for (const row of Array.isArray(taxRows) ? taxRows : []) {
    const cls = String(row.classification || row.type || "").toLowerCase();

    if (
      cls === "nft_transform_disposal" ||
      cls === "nft_transform_acquisition"
    ) {
      continue;
    }

    if (cls === "nft_transform") {
      const lifecycleKey = String(
        row.nftTransformLifecycleKey ||
          `${row.nftTransformBurnHash || row.hash || ""}|${
            row.nftTransformResultTokenId || row.tokenId || ""
          }`
      ).toLowerCase();
      if (usedTransformLifecycles.has(lifecycleKey)) continue;
      usedTransformLifecycles.add(lifecycleKey);
      out.push(toNftTransformDisplayParent(row));
      continue;
    }

    const isAcquisitionTax =
      cls === "nft_mint" ||
      cls === "mint" ||
      cls === "nft_galaxy_mint" ||
      cls === "galaxy_mint" ||
      cls === "nft_marketplace_purchase_payment";

    if (!isAcquisitionTax) {
      out.push(row);
      continue;
    }

    const children = resolveAcquisitionChildren(row, acquisitionRows);

    // ≥1 recoverable identity → always flatten to normal per-NFT mint rows
    if (children.length >= 1) {
      const displays = children.map((child) =>
        toNftTaxTableIdentityDisplayRow(child, row)
      );

      const parentTaxable = Number(
        row.taxableProfitEUR ?? row.taxableProfitFiat ?? row.taxableProfit ?? 0
      );
      if (Number.isFinite(parentTaxable) && displays.length > 0) {
        const headSum = displays
          .slice(0, -1)
          .reduce((s, r) => s + Number(r.taxableProfitEUR || 0), 0);
        const last = displays[displays.length - 1];
        const residual = Number((parentTaxable - headSum).toFixed(12));
        last.taxableProfit = residual;
        last.taxableProfitEUR = residual;
        last.taxableProfitFiat = residual;
      }

      for (const display of displays) {
        const key = pickNftIdentityKey(display);
        if (key) {
          if (usedIdentityKeys.has(key)) continue;
          usedIdentityKeys.add(key);
        }
        out.push(display);
      }
      continue;
    }

    // No children: keep tax row but never leave Token-ID blank when recoverable.
    const tid = recoverTokenId(row);
    if (tid != null) {
      out.push({
        ...row,
        tokenId: tid,
        nft_token_id: tid,
        token_id: tid,
        relatedMintTokenId: row.relatedMintTokenId ?? tid,
      });
    } else {
      out.push(row);
    }
  }

  // Orphan acquisition identities not linked to a displayed tax parent
  for (const row of Array.isArray(acquisitionRows) ? acquisitionRows : []) {
    const cls = String(row.classification || row.type || "").toLowerCase();
    // Transform result NFT is shown via nft_transform parent expand — never as mint row.
    if (
      cls === "nft_transform_acquisition" ||
      row.nftTransformKind ||
      row.nftTransformLifecycleKey
    ) {
      continue;
    }

    const key = pickNftIdentityKey(row);
    if (key && usedIdentityKeys.has(key)) continue;
    if (!key) continue;
    usedIdentityKeys.add(key);
    const already = out.some((r) => pickNftIdentityKey(r) === key);
    if (already) continue;

    // Attach matching payment parent FIFO when possible.
    const pay = String(row.nftBatchPaymentHash || row.paymentHash || "").toLowerCase();
    const parent =
      (Array.isArray(taxRows) ? taxRows : []).find((t) => {
        const th = String(t.nftBatchPaymentHash || t.hash || "").toLowerCase();
        return pay && th && pay === th;
      }) || null;

    out.push(toNftTaxTableIdentityDisplayRow(row, parent));
  }

  return out;
}

export function isNftBatchParentRow(row = {}, children = []) {
  const size = Number(row.nftBatchSize || 0);
  if (size > 1) return true;
  if ((children || []).length > 1) return true;
  const tokenId = row.tokenId ?? row.nft_token_id ?? row.token_id;
  const missingTokenId = tokenId == null || String(tokenId).trim() === "";
  return missingTokenId && (children || []).length > 0;
}

export function splitNftTaxAndAcquisitionRows(rows = []) {
  const allocationRows = [];
  const taxRows = [];

  for (const row of Array.isArray(rows) ? rows : []) {
    const cls = String(row?.classification || row?.type || "").toLowerCase();
    if (
      isNftAcquisitionAllocationRow(row) ||
      cls === "nft_transform_acquisition"
    ) {
      allocationRows.push(row);
    } else {
      taxRows.push(row);
    }
  }

  return { allocationRows, taxRows };
}

function economicEventIdFor(row = {}) {
  if (row.economicEventId) return row.economicEventId;

  const cls = String(row.classification || row.type || "").toLowerCase();
  if (
    cls === "nft_transform_acquisition" ||
    row.nftTransformLifecycleKey
  ) {
    if (row.nftTransformLifecycleKey) {
      return `nft-transform:${String(
        row.nftTransformLifecycleKey
      ).toLowerCase()}`;
    }
  }

  const paymentHash = String(
    row.nftBatchPaymentHash ||
      String(row.parentPaymentEventKey || "").split("|")[0] ||
      row.hash ||
      ""
  ).toLowerCase();
  if (!paymentHash || paymentHash === "unknown") return null;

  const matchType = String(
    row.acquisitionMatchType || row.paymentMode || row.costBasisSource || ""
  ).toLowerCase();
  if (matchType.includes("vemugs")) return `vemugs-brew:${paymentHash}`;
  return `nft-payment:${paymentHash}`;
}

function enrichAcquisitionRow(row = {}) {
  const cls = String(row.classification || row.type || "").toLowerCase();
  const isTransformAcquisition = cls === "nft_transform_acquisition";

  const paymentHash = isTransformAcquisition
    ? null
    : row.paymentHash ||
      row.nftBatchPaymentHash ||
      String(row.parentPaymentEventKey || "").split("|")[0] ||
      null;
  const mintHash =
    row.mintHash ||
    row.nftBatchMintHash ||
    row.acquisitionTxHash ||
    row.hash ||
    null;
  const batchSize = Number(row.nftBatchSize || row.allocationCount || 1) || 1;
  const paymentAmount = isTransformAcquisition
    ? null
    : row.paymentAmount ?? row.nftPaymentAmount ?? row.amount ?? null;

  const paymentAsset = isTransformAcquisition
    ? null
    : row.paymentAsset || row.nftPaymentToken || row.token || null;

  return {
    ...row,
    eventType: "nft_acquisition",
    ledgerRole: "nft_identity",
    taxExportRole: row.taxExportRole || "nft_acquisition_only",
    economicEventId: economicEventIdFor(row),
    collection:
      row.collection || row.nftName || row.nft_name || row.name || null,
    acquisitionType: isTransformAcquisition
      ? acquisitionTypeOf(row)
      : row.acquisitionType || null,
    paymentAsset,
    paymentAmount,
    paymentHash: paymentHash || null,
    mintHash,
    batchId: row.batchId || paymentHash || null,
    batchSize: row.batchSize ?? batchSize,
    batchIndex:
      row.batchIndex ??
      (Number.isFinite(Number(row.allocationIndex))
        ? Number(row.allocationIndex) + 1
        : 1),
    acquisitionFiatCurrency:
      row.acquisitionFiatCurrency ||
      row.fiatCurrency ||
      row.priceCurrency ||
      null,
    paymentTimestamp: isTransformAcquisition
      ? null
      : row.paymentTimestamp ?? row.acquisitionTimestamp ?? null,
    mintTimestamp: row.mintTimestamp ?? row.timestamp ?? null,
    nftTransformKind: row.nftTransformKind || null,
    nftTransformLifecycleKey: row.nftTransformLifecycleKey || null,
    nftTransformSourceNfts: Array.isArray(row.nftTransformSourceNfts)
      ? row.nftTransformSourceNfts
      : [],
    nftTransformResultNft: row.nftTransformResultNft || null,
    nftTransformTaxTreatment: row.taxTreatment || null,
    nftTransformValuationMethod: row.valuationMethod || null,
  };
}


function acquisitionPriority(row = {}) {
  const cls = String(row.classification || row.type || "").toLowerCase();

  if (cls === "nft_transform_acquisition") {
    return 100;
  }

  if (cls === "nft_marketplace_purchase_payment") {
    return 90;
  }

  const status = String(row.acquisitionStatus || "");
  const source = String(row.costBasisSource || "");

  const verifiedPaid =
    row.receiptVerified === true ||
    status === "paid_mint_tax_row" ||
    status === "receipt_mint_with_payment" ||
    status === "receipt_transfer_in_with_payment" ||
    [
      "matcher_payment_leg",
      "nft_acquisition_payment_row",
      "receipt_payment_leg",
      "receipt_aggregate_payment_split",
      "residual_receipt_payment_leg",
      "multi_mint_payment_allocation",
      "wov_offer_accepted_payment",
      "wov_direct_marketplace_payment",
      "wov_legacy_marketplace_payment",
    ].includes(source);

  if (verifiedPaid) {
    return 80;
  }

  if (isNftAcquisitionAllocationRow(row)) {
    return 70;
  }

  if (
    cls === "nft_mint" ||
    cls === "mint" ||
    cls === "nft_galaxy_mint" ||
    cls === "galaxy_mint"
  ) {
    return 50;
  }

  return 10;
}

function acquisitionHasKnownBasis(row = {}) {
  return (
    firstFiniteOrNull(
      row.nftAcquisitionCostFiat,
      row.nftAcquisitionCostEUR,
      row.acquisitionCostFiat,
      row.acquisitionCostEUR
    ) !== null
  );
}

export function buildNftAcquisitionRows({
  allocationRows = [],
  taxRows = [],
} = {}) {
  const byIdentity = new Map();
  const withoutIdentity = [];

  const upsert = (rawRow) => {
    if (!rawRow || typeof rawRow !== "object") {
      return;
    }

    const row = enrichAcquisitionRow(rawRow);
    const key = pickNftIdentityKey(row);

    if (!key) {
      withoutIdentity.push(row);
      return;
    }

    const current = byIdentity.get(key);

    if (!current) {
      byIdentity.set(key, row);
      return;
    }

    const nextPriority = acquisitionPriority(row);
    const currentPriority = acquisitionPriority(current);

    if (nextPriority > currentPriority) {
      byIdentity.set(key, row);
      return;
    }

    if (
      nextPriority === currentPriority &&
      !acquisitionHasKnownBasis(current) &&
      acquisitionHasKnownBasis(row)
    ) {
      byIdentity.set(key, row);
    }
  };

  for (const row of Array.isArray(allocationRows) ? allocationRows : []) {
    upsert(row);
  }

  for (const row of Array.isArray(taxRows) ? taxRows : []) {
    const cls = String(row.classification || row.type || "").toLowerCase();

    if (!ACQUISITION_TAX_CLASSES.has(cls)) {
      continue;
    }

    const isTransformAcquisition = cls === "nft_transform_acquisition";
    const batchSize = Number(row.nftBatchSize || 0);
    const batchIds = extractNftBatchTokenIds(row);

    if (batchSize > 1 || batchIds.length > 1) {
      continue;
    }

    const tokenId =
      row.tokenId ??
      row.nft_token_id ??
      row.token_id ??
      row.relatedMintTokenId;

    if (tokenId == null || String(tokenId).trim() === "") {
      continue;
    }

    const {
      fifoLegs: _FIFO_LEGS,
      matchedInflows: _MATCHED_INFLOWS,
      ...rest
    } = row;

    const hash = String(rest.hash || rest.txHash || "").toLowerCase();
    const token = String(rest.token || "").toUpperCase();

    upsert({
      ...rest,
      fifoLegs: [],
      nftPaymentToken: isTransformAcquisition
        ? null
        : rest.nftPaymentToken || rest.token,
      nftPaymentAmount: isTransformAcquisition
        ? null
        : rest.nftPaymentAmount ?? rest.amount,
      paymentAsset: isTransformAcquisition ? null : rest.paymentAsset,
      paymentAmount: isTransformAcquisition ? null : rest.paymentAmount,
      nftBatchSize: 1,
      allocationCount: 1,
      allocationIndex: 0,
      parentPaymentEventKey: isTransformAcquisition
        ? null
        : rest.parentPaymentEventKey ||
          (hash && token ? hash + "|" + token : null),
      nftBatchPaymentHash: isTransformAcquisition
        ? null
        : rest.nftBatchPaymentHash || rest.hash || null,
      nftBatchMintHash: rest.nftBatchMintHash || rest.hash || null,
    });
  }

  return [...byIdentity.values(), ...withoutIdentity];
}

function toIsoUtc(ts) {
  const n = Number(ts);
  if (!Number.isFinite(n) || n <= 0) return "";
  const ms = n > 1e12 ? n : n * 1000;
  try {
    return new Date(ms).toISOString();
  } catch {
    return "";
  }
}

function acquisitionTypeOf(row = {}) {
  const sourceClass = String(
    row.classification || row.sourceTaxClassification || row.type || ""
  ).toLowerCase();
  if (sourceClass === "nft_transform_acquisition") {
    const explicit = String(row.acquisitionType || "").toLowerCase();
    if (["remint", "melt", "nft_transform"].includes(explicit)) {
      return explicit;
    }
    const kind = String(row.nftTransformKind || "").toLowerCase();
    if (kind.includes("remint")) return "remint";
    if (kind.includes("melt")) return "melt";
    return "nft_transform";
  }

  const cls = String(
    row.acquisitionType ||
      row.classification ||
      row.sourceTaxClassification ||
      row.type ||
      ""
  ).toLowerCase();
  if (cls.includes("purchase") || cls.includes("marketplace")) return "purchase";
  return "mint";
}

export function toNftAcquisitionExportRow(row = {}, { fiatCurrency = "EUR" } = {}) {
  const isTransformAcquisition =
    String(row.classification || row.type || "").toLowerCase() ===
    "nft_transform_acquisition";
  const tokenId = String(
    row.tokenId ?? row.nft_token_id ?? row.token_id ?? ""
  ).trim();
  const batchSize = Number(row.batchSize ?? row.nftBatchSize ?? row.allocationCount ?? 1) || 1;
  const batchIndex =
    Number(
      row.batchIndex ??
        (Number.isFinite(Number(row.allocationIndex))
          ? Number(row.allocationIndex) + 1
          : 1)
    ) || 1;
  const paymentTs = isTransformAcquisition
    ? ""
    : row.paymentTimestamp ??
      row.acquisitionTimestamp ??
      row.timestamp ??
      "";
  const mintTs =
    row.mintTimestamp ?? row.timestamp ?? row.acquisitionTimestamp ?? "";
  const paymentHash = isTransformAcquisition
    ? ""
    : row.paymentHash || row.nftBatchPaymentHash || row.batchId || "";
  const mintHash = row.mintHash || row.nftBatchMintHash || row.hash || "";
  const acquisitionCostFiat = firstFiniteOrNull(
    row.acquisitionCostFiat,
    row.nftAcquisitionCostFiat,
    row.nftAcquisitionCostEUR,
    row.euroAmount
  );

  return {
    eventType: row.eventType || "nft_acquisition",
    collection: row.collection || row.nftName || row.nft_name || row.name || "",
    nftContract:
      row.nftContract || row.nft_contract || row.contract || row.contractAddress || "",
    tokenId,
    acquisitionType: acquisitionTypeOf(row),
    transformKind: row.nftTransformKind || "",
    paymentAsset: isTransformAcquisition
      ? ""
      : String(
          row.paymentAsset || row.nftPaymentToken || row.token || ""
        ).toUpperCase(),
    paymentAmount: isTransformAcquisition
      ? null
      : Number(row.paymentAmount ?? row.nftPaymentAmount ?? row.amount ?? 0),
    acquisitionCostFiat,
    acquisitionFiatCurrency: row.acquisitionFiatCurrency || fiatCurrency,
    paymentHash,
    mintHash,
    economicEventId: row.economicEventId || economicEventIdFor(row) || "",
    batchId: row.batchId || paymentHash || "",
    batchSize,
    batchIndex,
    batch: `${batchIndex}/${batchSize}`,
    paymentTimestamp: paymentTs,
    mintTimestamp: mintTs,
    paymentDateUtc: isTransformAcquisition ? "" : toIsoUtc(paymentTs),
    mintDateUtc: toIsoUtc(mintTs),
    reviewRequired: row.reviewRequired === true,
    reviewReason: row.reviewReason || "",
    sortTimestamp:
      Number(
        isTransformAcquisition ? mintTs || 0 : paymentTs || mintTs || 0
      ) || 0,
  };
}

export function buildNftAcquisitionExportRows(rows = [], opts = {}) {
  return (Array.isArray(rows) ? rows : [])
    .filter((row) => {
      const tokenId = String(row?.tokenId ?? row?.nft_token_id ?? row?.token_id ?? "").trim();
      return Boolean(tokenId);
    })
    .map((row) => toNftAcquisitionExportRow(row, opts))
    .sort((a, b) => {
      const ts = Number(b.sortTimestamp || 0) - Number(a.sortTimestamp || 0);
      if (ts) return ts;
      const batch = String(a.batchId || "").localeCompare(String(b.batchId || ""));
      if (batch) return batch;
      return Number(a.batchIndex || 0) - Number(b.batchIndex || 0);
    });
}

export function checkNftAcquisitionBatchInvariants(
  taxRows = [],
  acquisitionRows = []
) {
  const findings = [];

  for (const parent of Array.isArray(taxRows) ? taxRows : []) {
    const batchSize = Number(parent.nftBatchSize || 0);
    if (!(batchSize > 1)) continue;

    const children = matchAcquisitionChildren(parent, acquisitionRows);
    const uniqueIds = new Set(
      children.map((child) => pickNftIdentityKey(child)).filter(Boolean)
    );
    const childSum = children.reduce(
      (sum, child) =>
        sum + Number(child.nftPaymentAmount ?? child.paymentAmount ?? child.amount ?? 0),
      0
    );
    const parentTotal = Number(
      parent.nftBatchTotalPaymentVtho ?? parent.amount ?? 0
    );
    const paymentHash = String(
      parent.nftBatchPaymentHash || parent.hash || ""
    ).toLowerCase();

    if (children.length !== batchSize) {
      findings.push({
        code: "CHILD_COUNT",
        paymentHash,
        expected: batchSize,
        actual: children.length,
      });
    }

    if (
      Number.isFinite(parentTotal) &&
      parentTotal > 0 &&
      Math.abs(childSum - parentTotal) > AMOUNT_EPS
    ) {
      findings.push({
        code: "CHILD_PAYMENT_SUM",
        paymentHash,
        expected: parentTotal,
        actual: childSum,
      });
    }

    if (uniqueIds.size !== batchSize) {
      findings.push({
        code: "UNIQUE_IDENTITIES",
        paymentHash,
        expected: batchSize,
        actual: uniqueIds.size,
      });
    }
  }

  return findings;
}

export function logNftAcquisitionBatchInvariants(
  taxRows = [],
  acquisitionRows = []
) {
  const findings = checkNftAcquisitionBatchInvariants(taxRows, acquisitionRows);
  for (const finding of findings) {
    console.warn("⚠️ NFT acquisition ledger invariant failed", finding);
  }
  if (findings.length === 0) {
    const batchParents = (taxRows || []).filter(
      (row) => Number(row.nftBatchSize || 0) > 1
    );
    if (batchParents.length > 0) {
      console.log(
        `✅ NFT acquisition ledger invariants ok (${batchParents.length} batch parent(s), ${acquisitionRows.length} identity row(s))`
      );
    }
  }
  return findings;
}
